import React, { useEffect, useMemo, useState } from "react";
import {
  Col, Row, Card, CardBody, Badge, Button, Modal, ModalHeader, ModalBody,
  ModalFooter, Spinner, Alert, Table, Input, Label, FormGroup, InputGroup, InputGroupText
} from "reactstrap";
import Select from "react-select";
import TableContainer from "../../../components/Common/TableContainer";
import Breadcrumbs from "../../../components/Common/Breadcrumb";
import { API_BASE, getAuthHeaders } from "../../../helpers/apiConfig";
import { downloadObjectsCsv } from "../../../helpers/csvExport";

// "10.000,50" -> 10000.5  ya da  "10000.50" -> 10000.5
const trToFloat = (str) => {
  if (str == null || str === "") return 0;
  const s = String(str).replace(/\./g, "").replace(",", ".");
  const n = parseFloat(s);
  return isNaN(n) ? 0 : n;
};

// 10000.5 -> "10.000,50"  (input'a yazılırken)
const floatToTr = (val) => {
  if (val == null || val === "") return "";
  const n = typeof val === "number" ? val : trToFloat(val);
  return n.toLocaleString("tr-TR", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const BASE_URL = process.env.NODE_ENV === "development" ? "" : API_BASE;

const KullaniciCuzdanlari = () => {
  document.title = "Kullanıcı Cüzdanları | HarmanaGEL";

  const [cuzdanlar, setCuzdanlar] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  const [detailModal, setDetailModal] = useState(false);
  const [selectedCuzdan, setSelectedCuzdan] = useState(null);
  const [hareketler, setHareketler] = useState([]);
  const [hareketLoading, setHareketLoading] = useState(false);

  const [yukleModal, setYukleModal] = useState(false);
  const [yuklenecekCuzdan, setYuklenecekCuzdan] = useState(null);
  // Miktar TR formatlı string olarak tutulur, gönderirken trToFloat ile çevrilir
  const [yukleMiktar, setYukleMiktar] = useState("10.000,00");
  const [yukleAciklama, setYukleAciklama] = useState("");
  const [yukleLoading, setYukleLoading] = useState(false);
  const [islemModu, setIslemModu] = useState("yukle"); // "yukle" | "dus"
  const [kullanicilar, setKullanicilar] = useState([]);
  const [secilenKullaniciId, setSecilenKullaniciId] = useState("");

  const fetchCuzdanlar = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await fetch(
        `${BASE_URL}/KULLANICI_CUZDAN?$top=2000&$orderby=BAKIYE desc&$expand=KULLANICILAR($select=AD_SOYAD,Mail_Adress,CEP_NO)`,
        { headers: getAuthHeaders() }
      );
      const result = await response.json();
      if (result && result.value) setCuzdanlar(result.value);
    } catch (err) {
      setError("Cüzdanlar yüklenirken hata oluştu.");
    } finally {
      setIsLoading(false);
    }
  };

  const fetchKullanicilar = async () => {
    try {
      const response = await fetch(
        `${BASE_URL}/KULLANICILAR?$top=1000&$select=ID,AD_SOYAD,Mail_Adress,CEP_NO&$orderby=AD_SOYAD`,
        { headers: getAuthHeaders() }
      );
      const result = await response.json();
      if (result && result.value) setKullanicilar(result.value);
    } catch (err) {
      console.error("Kullanıcılar yüklenirken hata:", err);
    }
  };

  useEffect(() => {
    fetchCuzdanlar();
    fetchKullanicilar();
  }, []);

  const fetchHareketler = async (kullaniciId) => {
    setHareketLoading(true);
    try {
      const response = await fetch(
        `${BASE_URL}/KULLANICI_CUZDAN_HAREKETLERI?$top=100&$filter=KULLANICI_ID eq ${kullaniciId}&$orderby=ISLEM_TARIHI desc`,
        { headers: getAuthHeaders() }
      );
      const result = await response.json();
      if (result && result.value) setHareketler(result.value);
    } catch (err) {
      setError("Hareketler yüklenirken hata oluştu.");
    } finally {
      setHareketLoading(false);
    }
  };

  const handleViewDetail = (cuzdan) => {
    setSelectedCuzdan(cuzdan);
    setHareketler([]);
    setDetailModal(true);
    fetchHareketler(cuzdan.KULLANICI_ID);
  };

  // Aynı modal iki işlem için kullanılır: "yukle" | "dus"
  // (backend: api/AdminCuzdan/BakiyeYukle | BakiyeDusur — ikisi de atomic + bildirimli)
  const handleOpenYukle = (cuzdan, mod = "yukle") => {
    setIslemModu(mod);
    setYuklenecekCuzdan(cuzdan);
    if (!cuzdan) setSecilenKullaniciId("");
    setYukleMiktar(mod === "dus" ? "" : "10.000,00");
    setYukleAciklama("");
    setYukleModal(true);
  };

  // react-select options listesi
  const kullaniciOptions = useMemo(
    () => kullanicilar.map((k) => ({
      value: k.ID,
      label: k.AD_SOYAD || `Kullanıcı #${k.ID}`,
      ad: k.AD_SOYAD,
      mail: k.Mail_Adress,
      cep: k.CEP_NO,
    })),
    [kullanicilar]
  );

  // Arama: ad, mail veya cep numarası eşleşsin
  const kullaniciFilterOption = (option, rawInput) => {
    if (!rawInput) return true;
    const q = rawInput.toLocaleLowerCase("tr-TR");
    const d = option.data || {};
    const hay = `${d.ad || ""} ${d.mail || ""} ${d.cep || ""}`.toLocaleLowerCase("tr-TR");
    return hay.includes(q);
  };

  const renderKullaniciOption = (data) => (
    <div>
      <div className="fw-medium">{data.ad || `Kullanıcı #${data.value}`}</div>
      <small className="text-muted">
        {data.mail || "—"}
        {data.cep ? `  ·  ${data.cep}` : ""}
      </small>
    </div>
  );

  const handleYukle = async () => {
    const miktar = trToFloat(yukleMiktar);
    if ((!yuklenecekCuzdan && !secilenKullaniciId) || !miktar || miktar <= 0) {
      setError("Geçerli bir kullanıcı ve miktar giriniz.");
      return;
    }

    setYukleLoading(true);
    setError(null);

    try {
      const kullaniciId = yuklenecekCuzdan
        ? yuklenecekCuzdan.KULLANICI_ID
        : parseInt(secilenKullaniciId);

      const dusuyor = islemModu === "dus";
      // Atomic backend endpoint: BAKIYE update + HAREKET insert + BILDIRIM + FCM push
      // BakiyeDusur ayrıca yetersiz bakiyeyi reddeder.
      const res = await fetch(
        `${BASE_URL}/api/AdminCuzdan/${dusuyor ? "BakiyeDusur" : "BakiyeYukle"}`,
        {
          method: "POST",
          headers: getAuthHeaders(),
          body: JSON.stringify(
            dusuyor
              ? {
                  KullaniciId: kullaniciId,
                  Miktar: miktar,
                  Aciklama: yukleAciklama || "",
                }
              : {
                  KullaniciId: kullaniciId,
                  Miktar: miktar,
                  Aciklama: yukleAciklama || "",
                  IslemTipi: "Yatırım",
                }
          ),
        }
      );

      if (!res.ok) {
        const errText = await res.text();
        let msg = dusuyor ? "Bakiye düşülemedi." : "Bakiye yüklenemedi.";
        try {
          const errJson = JSON.parse(errText);
          msg = errJson.Message || errJson.message || errJson.ExceptionMessage || msg;
        } catch {
          if (errText) msg = errText;
        }
        throw new Error(msg);
      }

      setYukleModal(false);
      setYuklenecekCuzdan(null);
      setSecilenKullaniciId("");
      fetchCuzdanlar();
    } catch (err) {
      setError(err.message || "Bakiye yüklenirken hata oluştu.");
    } finally {
      setYukleLoading(false);
    }
  };

  const islemBadge = (tip) => {
    const renkler = {
      "Yatırım": "success",
      "Çekim": "danger",
      "Satış": "info",
      "İade": "warning",
      "Komisyon": "secondary",
    };
    return <Badge color={renkler[tip] || "secondary"}>{tip || "-"}</Badge>;
  };

  const durumBadge = (durum) => {
    const renkler = { "Bekliyor": "warning", "Onaylandı": "success", "Reddedildi": "danger", "Tamamlandı": "success" };
    return <Badge color={renkler[durum] || "secondary"}>{durum || "-"}</Badge>;
  };

  const toplamBakiye = useMemo(() => cuzdanlar.reduce((s, c) => s + parseFloat(c.BAKIYE || 0), 0), [cuzdanlar]);
  const toplamKullanici = cuzdanlar.length;
  const pozitifBakiye = useMemo(() => cuzdanlar.filter(c => parseFloat(c.BAKIYE || 0) > 0).length, [cuzdanlar]);

  const columns = useMemo(() => [
    {
      header: "ID",
      accessorKey: "ID",
      enableSorting: true,
      enableColumnFilter: false,
      cell: (cell) => <span className="fw-bold">#{cell.getValue()}</span>,
    },
    {
      header: "Kullanıcı",
      accessorKey: "KULLANICILAR",
      enableSorting: false,
      enableColumnFilter: true,
      cell: (cell) => {
        const u = cell.getValue();
        return u ? (
          <div>
            <div className="fw-medium">{u.AD_SOYAD}</div>
            <small className="text-muted">{u.Mail_Adress}</small>
          </div>
        ) : (
          <span className="text-muted">Kullanıcı #{cell.row.original.KULLANICI_ID}</span>
        );
      },
    },
    {
      header: "Telefon",
      accessorKey: "CEP_NO",
      enableSorting: false,
      enableColumnFilter: false,
      cell: (cell) => cell.row.original.KULLANICILAR?.CEP_NO || "-",
    },
    {
      header: "Bakiye",
      accessorKey: "BAKIYE",
      enableSorting: true,
      enableColumnFilter: false,
      cell: (cell) => {
        const val = parseFloat(cell.getValue() || 0);
        return (
          <span className={`fw-bold fs-5 ${val >= 0 ? "text-success" : "text-danger"}`}>
            {val.toLocaleString("tr-TR", { minimumFractionDigits: 2 })} ₺
          </span>
        );
      },
    },
    {
      header: "Son Güncelleme",
      accessorKey: "SON_GUNCELLEME",
      enableSorting: true,
      enableColumnFilter: false,
      cell: (cell) => {
        const d = cell.getValue();
        return d ? new Date(d).toLocaleDateString("tr-TR") : "-";
      },
    },
    {
      header: "İşlemler",
      enableSorting: false,
      enableColumnFilter: false,
      cell: (cellProps) => {
        const c = cellProps.row.original;
        return (
          <div className="d-flex gap-1">
            <Button color="success" size="sm" outline onClick={() => handleOpenYukle(c, "yukle")}>
              <i className="bx bx-plus-circle me-1" /> Yükle
            </Button>
            <Button color="danger" size="sm" outline onClick={() => handleOpenYukle(c, "dus")}
              disabled={!(Number(c.BAKIYE) > 0)}>
              <i className="bx bx-minus-circle me-1" /> Düş
            </Button>
            <Button color="info" size="sm" outline onClick={() => handleViewDetail(c)}>
              <i className="bx bx-detail me-1" /> Hareketler
            </Button>
          </div>
        );
      },
    },
  ], []);

  return (
    <React.Fragment>
      <div className="page-content">
        <div className="container-fluid">
          <Breadcrumbs title="HarmanaGEL" breadcrumbItem="Kullanıcı Cüzdanları" />

          {error && <Alert color="danger" toggle={() => setError(null)}>{error}</Alert>}

          <Row className="mb-3">
            <Col md={4}>
              <Card className="bg-primary text-white">
                <CardBody className="d-flex align-items-center gap-3">
                  <i className="bx bx-wallet fs-1" />
                  <div>
                    <h5 className="mb-0">{toplamBakiye.toLocaleString("tr-TR", { minimumFractionDigits: 2 })} ₺</h5>
                    <small>Toplam Sistem Bakiyesi</small>
                  </div>
                </CardBody>
              </Card>
            </Col>
            <Col md={4}>
              <Card className="bg-info text-white">
                <CardBody className="d-flex align-items-center gap-3">
                  <i className="bx bx-user fs-1" />
                  <div>
                    <h5 className="mb-0">{toplamKullanici}</h5>
                    <small>Toplam Cüzdan Sayısı</small>
                  </div>
                </CardBody>
              </Card>
            </Col>
            <Col md={4}>
              <Card className="bg-success text-white">
                <CardBody className="d-flex align-items-center gap-3">
                  <i className="bx bx-up-arrow-circle fs-1" />
                  <div>
                    <h5 className="mb-0">{pozitifBakiye}</h5>
                    <small>Pozitif Bakiyeli Kullanıcı</small>
                  </div>
                </CardBody>
              </Card>
            </Col>
          </Row>

          <Row>
            <Col lg={12}>
              <Card>
                <CardBody>
                  <div className="d-flex justify-content-between align-items-center mb-3">
                    <h4 className="card-title mb-0">Kullanıcı Cüzdanları</h4>
                    <div className="d-flex gap-2">
                      <Button color="success" size="sm" onClick={() => handleOpenYukle(null)}>
                        <i className="bx bx-plus me-1" />
                        Yeni Kullanıcıya Bakiye Yükle
                      </Button>
                      <Button
                        color="secondary"
                        size="sm"
                        outline
                        onClick={() =>
                          downloadObjectsCsv(
                            `cuzdanlar-${new Date().toISOString().slice(0, 10)}.csv`,
                            cuzdanlar,
                            [
                              { key: "ID", label: "Cüzdan ID" },
                              { key: "KULLANICI_ID", label: "Kullanıcı ID" },
                              {
                                label: "Ad Soyad",
                                get: (c) => c.KULLANICILAR?.AD_SOYAD || "",
                              },
                              {
                                label: "E-posta",
                                get: (c) => c.KULLANICILAR?.Mail_Adress || "",
                              },
                              { key: "BAKIYE", label: "Bakiye" },
                            ]
                          )
                        }
                        disabled={!cuzdanlar.length}
                      >
                        <i className="bx bx-export me-1" />
                        CSV
                      </Button>
                      <Button color="primary" size="sm" onClick={fetchCuzdanlar} disabled={isLoading}>
                        <i className="bx bx-refresh me-1" />
                        Yenile
                      </Button>
                    </div>
                  </div>

                  {isLoading ? (
                    <div className="text-center py-5"><Spinner color="primary" /></div>
                  ) : (
                    <TableContainer
                      columns={columns}
                      data={cuzdanlar}
                      isGlobalFilter={true}
                      SearchPlaceholder="Kullanıcı ara..."
                      isPagination={true}
                      isCustomPageSize={true}
                      tableClass="table-bordered align-middle nowrap w-100 mb-0"
                      theadClass="table-light"
                      paginationWrapper="dataTables_paginate paging_simple_numbers pagination-rounded"
                    />
                  )}
                </CardBody>
              </Card>
            </Col>
          </Row>
        </div>
      </div>

      {/* Bakiye Yükle Modal */}
      <Modal
        isOpen={yukleModal}
        toggle={() => setYukleModal(false)}
        centered
        scrollable
      >
        <ModalHeader toggle={() => setYukleModal(false)}>
          <i className={`bx bx-wallet me-2 ${islemModu === "dus" ? "text-danger" : "text-success"}`} />
          {islemModu === "dus" ? "Bakiye Düş" : "Bakiye Yükle"}
        </ModalHeader>
        <ModalBody style={{ overflow: "visible" }}>
          {yuklenecekCuzdan ? (
            <div className="alert alert-light border mb-3">
              <div className="d-flex justify-content-between align-items-center">
                <span className="text-muted">Kullanıcı:</span>
                <strong>{yuklenecekCuzdan?.KULLANICILAR?.AD_SOYAD || `Kullanıcı #${yuklenecekCuzdan?.KULLANICI_ID}`}</strong>
              </div>
              <div className="d-flex justify-content-between align-items-center mt-1">
                <span className="text-muted">Mevcut Bakiye:</span>
                <strong className="text-primary">
                  {parseFloat(yuklenecekCuzdan?.BAKIYE || 0).toLocaleString("tr-TR", { minimumFractionDigits: 2 })} ₺
                </strong>
              </div>
            </div>
          ) : (
            <FormGroup className="mb-3">
              <Label className="fw-bold">
                <i className="bx bx-user me-1 text-primary" />
                Kullanıcı Seçin
              </Label>
              <Select
                placeholder="Ad, e-posta veya telefon ile ara..."
                noOptionsMessage={() => "Eşleşen kullanıcı yok"}
                options={kullaniciOptions}
                value={kullaniciOptions.find((o) => String(o.value) === String(secilenKullaniciId)) || null}
                onChange={(opt) => setSecilenKullaniciId(opt ? opt.value : "")}
                filterOption={kullaniciFilterOption}
                formatOptionLabel={renderKullaniciOption}
                isClearable
                isSearchable
                maxMenuHeight={240}
                menuPlacement="auto"
                menuPosition="fixed"
                menuPortalTarget={typeof document !== "undefined" ? document.body : null}
                styles={{
                  menuPortal: (b) => ({ ...b, zIndex: 9999 }),
                  menu: (b) => ({ ...b, zIndex: 9999, backgroundColor: "#fff", boxShadow: "0 4px 18px rgba(0,0,0,0.15)" }),
                  option: (b, state) => ({
                    ...b,
                    backgroundColor: state.isSelected ? "#556ee6" : state.isFocused ? "#f5f6fa" : "#fff",
                    color: state.isSelected ? "#fff" : "#495057",
                    cursor: "pointer",
                    padding: "8px 12px",
                  }),
                  control: (b) => ({ ...b, minHeight: 40 }),
                }}
              />
            </FormGroup>
          )}

          <FormGroup>
            <Label className="fw-bold">
              <i className={`bx bx-money me-1 ${islemModu === "dus" ? "text-danger" : "text-success"}`} />
              {islemModu === "dus" ? "Düşülecek Miktar (₺)" : "Yüklenecek Miktar (₺)"}
            </Label>
            <InputGroup>
              <Input
                type="text"
                inputMode="decimal"
                placeholder="0,00"
                value={yukleMiktar}
                onChange={(e) => {
                  // Sadece rakam, nokta ve virgüle izin ver
                  const cleaned = e.target.value.replace(/[^\d.,]/g, "");
                  setYukleMiktar(cleaned);
                }}
                onBlur={() => {
                  const n = trToFloat(yukleMiktar);
                  if (n > 0) setYukleMiktar(floatToTr(n));
                }}
              />
              <InputGroupText>₺</InputGroupText>
            </InputGroup>
            {yukleMiktar && (
              <small className="text-muted">
                Tutar: {floatToTr(trToFloat(yukleMiktar))} ₺
              </small>
            )}
          </FormGroup>

          <FormGroup>
            <Label className="fw-bold">
              <i className="bx bx-comment-detail me-1 text-muted" />
              Açıklama
            </Label>
            <Input
              type="textarea"
              rows="2"
              placeholder={islemModu === "dus" ? "Admin tarafından bakiyeden düşüldü" : "Admin tarafından bakiye yüklendi"}
              value={yukleAciklama}
              onChange={(e) => setYukleAciklama(e.target.value)}
            />
          </FormGroup>
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" outline onClick={() => setYukleModal(false)} disabled={yukleLoading}>
            İptal
          </Button>
          <Button color={islemModu === "dus" ? "danger" : "success"} onClick={handleYukle} disabled={yukleLoading}>
            {yukleLoading ? <Spinner size="sm" className="me-1" /> : <i className="bx bx-check me-1" />}
            {islemModu === "dus" ? "Bakiye Düş" : "Bakiye Yükle"}
          </Button>
        </ModalFooter>
      </Modal>

      {/* Hareketler Modal */}
      <Modal isOpen={detailModal} toggle={() => setDetailModal(false)} size="lg" centered>
        <ModalHeader toggle={() => setDetailModal(false)}>
          Cüzdan Hareketleri: {selectedCuzdan?.KULLANICILAR?.AD_SOYAD || `Kullanıcı #${selectedCuzdan?.KULLANICI_ID}`}
        </ModalHeader>
        <ModalBody>
          <div className="mb-3 d-flex justify-content-between align-items-center">
            <h6 className="mb-0">
              Mevcut Bakiye:{" "}
              <span className="fw-bold text-success fs-5">
                {parseFloat(selectedCuzdan?.BAKIYE || 0).toLocaleString("tr-TR", { minimumFractionDigits: 2 })} ₺
              </span>
            </h6>
            <small className="text-muted">Son {hareketler.length} işlem gösteriliyor</small>
          </div>

          {hareketLoading ? (
            <div className="text-center py-4"><Spinner color="primary" /></div>
          ) : hareketler.length === 0 ? (
            <Alert color="info">Bu kullanıcıya ait cüzdan hareketi bulunamadı.</Alert>
          ) : (
            <div className="table-responsive">
              <Table className="table-bordered table-sm align-middle">
                <thead className="table-light">
                  <tr>
                    <th>ID</th>
                    <th>İşlem Tipi</th>
                    <th>Miktar</th>
                    <th>Açıklama</th>
                    <th>Tarih</th>
                    <th>Durum</th>
                  </tr>
                </thead>
                <tbody>
                  {hareketler.map((h) => (
                    <tr key={h.ID}>
                      <td>#{h.ID}</td>
                      <td>{islemBadge(h.ISLEM_TIPI)}</td>
                      <td className={`fw-bold ${parseFloat(h.MIKTAR || 0) >= 0 ? "text-success" : "text-danger"}`}>
                        {parseFloat(h.MIKTAR || 0).toLocaleString("tr-TR", { minimumFractionDigits: 2 })} ₺
                      </td>
                      <td>{h.ACIKLAMA || "-"}</td>
                      <td>{h.ISLEM_TARIHI ? new Date(h.ISLEM_TARIHI).toLocaleString("tr-TR") : "-"}</td>
                      <td>{durumBadge(h.DURUM)}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          )}
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" outline onClick={() => setDetailModal(false)}>Kapat</Button>
        </ModalFooter>
      </Modal>
    </React.Fragment>
  );
};

export default KullaniciCuzdanlari;
